import { z } from "zod";
import { zEmail, zId, zMoney } from "./common.js";

export const AUCTION_STATUSES = ["draft", "preview", "live", "closed", "settled"] as const;
export const zAuctionStatus = z.enum(AUCTION_STATUSES);
export type AuctionStatus = z.infer<typeof zAuctionStatus>;

export const zCreateAuction = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(5000).optional(),
  startsAt: z.string().datetime({ offset: true }),
  endsAt: z.string().datetime({ offset: true }),
  fundId: zId.optional(),
  campaignId: zId.nullable().optional(),
  eventId: zId.nullable().optional(),
  /** anti-sniping: a bid in the final N minutes extends the close (0 = off) */
  extendMinutes: z.number().int().min(0).max(30).default(2),
});
export type CreateAuction = z.infer<typeof zCreateAuction>;

export const zUpdateAuction = zCreateAuction.partial().extend({
  status: zAuctionStatus.optional(),
});
export type UpdateAuction = z.infer<typeof zUpdateAuction>;

export const zUpsertAuctionItem = z.object({
  title: z.string().trim().min(1).max(200),
  description: z.string().max(5000).optional(),
  category: z.string().trim().max(80).optional(),
  /** fair market value — the non-deductible portion of a winning bid */
  fairMarketValue: zMoney,
  startingBid: zMoney,
  minIncrement: zMoney.default(500),
  buyNowPrice: zMoney.nullable().optional(),
  /** who donated the item; with recordInKind, books an in-kind gift at FMV */
  donorConstituentId: zId.nullable().optional(),
  recordInKind: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
});
export type UpsertAuctionItem = z.infer<typeof zUpsertAuctionItem>;

/** public bidder registration (dedupes/creates a constituent by email) */
export const zRegisterBidder = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: zEmail,
  phone: z.string().trim().max(30).optional(),
});
export type RegisterBidder = z.infer<typeof zRegisterBidder>;

/** a bid: `amount` is the visible bid; `maxAmount` arms proxy bidding up to that ceiling */
export const zPlaceBid = z.object({
  token: z.string().min(10).max(200),
  itemId: zId,
  amount: zMoney,
  maxAmount: zMoney.optional(),
});
export type PlaceBid = z.infer<typeof zPlaceBid>;

export const zBuyNow = z.object({
  token: z.string().min(10).max(200),
  itemId: zId,
});

export const zPaddleRaise = z.object({
  token: z.string().min(10).max(200),
  amount: zMoney,
});

/** staff-entered in-room paddle raise */
export const zStaffPaddleRaise = z.object({
  bidderNumber: z.number().int().min(1),
  amount: zMoney,
});

/** winner pays all unpaid winnings (dev payment adapter until 6.1 lands) */
export const zAuctionCheckout = z.object({
  token: z.string().min(10).max(200),
  paymentToken: z.string().min(1).max(500),
});
